<template>
  <section ref="root" class="ch">
    <div class="ch__label">Cambiar plan</div>
    <p class="ch__hint">Los cambios de plan aplican a partir del siguiente ciclo de facturación.</p>

    <div v-if="overview.requestedPlan" class="req">
      <span class="mdi mdi-clock-outline"></span>
      <span class="req__text">
        Pediste cambiar al <strong>Plan {{ overview.requestedPlan.name }}</strong>. Parkos lo aplicará en tu siguiente cobro<span v-if="overview.nextCharge?.date"> ({{ formatShortDate(overview.nextCharge.date) }})</span>.
      </span>
      <button v-if="!readOnly" class="req__cancel" :disabled="busy" @click="$emit('cancel-request')">Cancelar solicitud</button>
    </div>

    <div v-if="overview.plans.length" class="ch__row">
      <button
        v-for="p in overview.plans"
        :key="p.id"
        class="pl"
        :class="{ 'pl--current': p.current, 'pl--requested': overview.requestedPlan?.id === p.id }"
        :disabled="p.current || readOnly"
        @click="$emit('select', p)"
      >
        <span v-if="p.current" class="pl__tag">Plan actual</span>
        <span v-else-if="overview.requestedPlan?.id === p.id" class="pl__tag pl__tag--amber">Solicitado</span>
        <span class="pl__name">{{ p.name }}</span>
        <span class="pl__price"><span class="pl__num">${{ formatMoney(p.monthlyPrice) }}</span><span class="pl__per">/mes</span></span>
        <ul class="pl__list">
          <li><span class="mdi mdi-check"></span>{{ p.maxPlots ? `Hasta ${p.maxPlots} naves` : 'Naves ilimitadas' }}</li>
          <li><span class="mdi mdi-check"></span>{{ p.maxUsers ? `Hasta ${p.maxUsers} usuarios` : 'Usuarios ilimitados' }}</li>
          <li v-for="f in p.features.slice(0, 2)" :key="f"><span class="mdi mdi-check"></span>{{ f }}</li>
        </ul>
      </button>
    </div>
    <div v-else class="ch__empty">Por ahora no hay otros planes disponibles. Contacta a Parkos.</div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { formatShortDate, formatMoney } from './planFormat'

defineProps({ overview: { type: Object, required: true }, busy: Boolean, readOnly: Boolean })
defineEmits(['select', 'cancel-request'])

const root = ref(null)
defineExpose({ scrollIntoView: () => root.value?.scrollIntoView({ behavior: 'smooth', block: 'start' }) })
</script>

<style scoped>
.ch { scroll-margin-top: 16px; }
.ch__label { font: 500 12px var(--font-body); color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
.ch__hint { margin: 4px 0 14px; font: 400 11px var(--font-body); color: #64748b; }
.req { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; padding: 10px 12px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; color: #b45309; }
.req__text { flex: 1; font: 400 12px/1.45 var(--font-body); }
.req__cancel { background: none; border: none; cursor: pointer; font: 500 12px var(--font-body); color: #b45309; text-decoration: underline; white-space: nowrap; }
.ch__row { display: flex; flex-wrap: wrap; gap: 12px; }
.pl { position: relative; width: 160px; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; padding: 16px 12px 14px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; text-align: left; cursor: pointer; box-sizing: border-box; transition: border-color 0.15s, box-shadow 0.15s; }
.pl:hover:not(:disabled) { border-color: #93c5fd; box-shadow: 0 2px 10px rgba(59, 130, 246, 0.12); }
.pl:disabled { cursor: default; }
.pl--current { border: 2px solid #3b82f6; padding: 15px 11px 13px; }
.pl--requested { border: 2px solid #f0a500; padding: 15px 11px 13px; }
.pl__tag { position: absolute; top: 8px; right: 8px; padding: 1px 6px; border-radius: 4px; background: #dbeafe; color: #1d4ed8; font: 500 9px var(--font-body); }
.pl__tag--amber { background: #fef3c7; color: #b45309; }
.pl__name { margin-top: 10px; font: 700 14px var(--font-display); color: #1e3a5f; }
.pl__price { display: flex; align-items: baseline; gap: 2px; }
.pl__num { font: 700 18px var(--font-mono); color: #1e3a5f; }
.pl__per { font: 400 12px var(--font-body); color: #64748b; }
.pl__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
.pl__list li { display: flex; align-items: flex-start; gap: 5px; font: 400 11px/1.35 var(--font-body); color: #64748b; }
.pl__list .mdi { color: #10b981; font-size: 13px; line-height: 1.2; }
.ch__empty { font: 400 12px var(--font-body); color: #94a3b8; }
</style>